import { GlobalComponentService } from '@techmore/global';
import { environment } from 'src/environments/environment';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, BehaviorSubject, interval, Subject, throwError, of } from 'rxjs';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from 'src/app/web-generator/generator-env-token';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, tap, filter, switchMap, delay, timeout } from 'rxjs/operators';
import { MEMBER_LOGIN_WAY } from '../../entity/member-login-way';
import { AbstractLoginService } from '../../declaration/abstract-member-service';
import { LoginResultCode } from '../../declaration/LoginResultCode.enum';
import { CommonHttpClientService } from 'src/app/share/service/common-http-client.service';
import { ServerConfigService } from 'src/app/share/service/server-config.service';
import { TranslateService } from '@ngx-translate/core';


/**
 * 文化部會員中心登入服務
 *
 * 登入流程:
 * 開啟文化部會員登入對話視窗 --> 登入後轉導回網站 memberLoading 頁面，取得 authCode --> 用 authCode 向 seerver 取得會員資料
 */
@Injectable()
export class CultureCenterService extends AbstractLoginService {
  isBrowser: boolean;
  protected _isRefreshing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  memerSystemId$ = new BehaviorSubject<string>('NMTH_TCMB');
  /** 文化部會員中心 ID */
  get memerSystemId() { return this.memerSystemId$.value; }

  constructor(
    @Inject(GENERATOR_ENV_TOKEN) protected generatorEnv: GeneratorEnv,
    protected http: CommonHttpClientService,
    protected serverConfigService: ServerConfigService,
    @Inject(PLATFORM_ID) private platformId: object,
    protected globalComponentService: GlobalComponentService,
    protected translateService: TranslateService,
  ) {
    super(generatorEnv);
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  init() {
    if (this.isBrowser) {
      this.loadMemberInfoFromStorage();
    }
    this.initFinish$.next(true);
  }

  /** 將 sessionStorage 裡已有的auth_token載入到服務裡 */
  loadMemberInfoFromStorage() {
    let data: any;
    data = localStorage.getItem('auth_token');
    if (localStorage.getItem('auth_way') === MEMBER_LOGIN_WAY.CULTURE_CENTER && data) {
      try {
        const memberInfo = this.parseToken(data);
        if (JSON.stringify(memberInfo) !== JSON.stringify(this.memberInfo$.value)) {
          this.memberInfo$.next(memberInfo);
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  login(): Observable<any> {
    this.onLoginStart$.next();
    const openedWindow = window.open(`${environment.memberWeb}`,
      '_blank',
      'height=500,resizable=yes,scrollbars=yes,width=700');

    return new Observable(subscriber => {
      const checkWinClosedSubc = interval(1000).subscribe(() => {
        if (openedWindow.closed) {
          if (window['loginErrorReason']) {
            const error = window['loginErrorReason'];
            delete window['loginErrorReason'];
            this.onLoginEnd$.next(LoginResultCode.fail);
            checkWinClosedSubc.unsubscribe();
            subscriber.error(error);
            subscriber.complete();
          } else {
            if (localStorage.getItem('auth_token')) {
              this.loadMemberInfoFromStorage();
              this.onLoginEnd$.next(LoginResultCode.success);
              subscriber.next(true);
            } else {
              this.onLoginEnd$.next(LoginResultCode.cancel);
              subscriber.next(false);
            }
            checkWinClosedSubc.unsubscribe();
            subscriber.complete();
          }
        }
      });
    });
  }

  logout(fromUser = true): Observable<any> {
    if (fromUser) {
      // https://member.moc.gov.tw/MOCMC/M0003/ajLogout
      // http://membertest.moc.gov.tw/MOCMC/M0003/logout?SYS_ID=MOCCMSDB
      window.open(environment.memberLogout,
        '_blank',
        'height=500,resizable=yes,scrollbars=yes,width=700');
    }

    return new Observable<any>((subscriber) => {
      localStorage.removeItem('auth_code');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_refresh_token');
      localStorage.removeItem('auth_way');
      if (fromUser) {
        this.logout$.next();
      }
      this.memberInfo$.next(undefined);
      subscriber.next(true);
    });
  }

  getMembertoken(authCode: string): Observable<any> {
    const obsFunc = () => {
      const body = new URLSearchParams();
      body.set('authCode', authCode);
      body.set('lang', this.translateService.currentLang);
      return this.http.post(`${this.generatorEnv.apiHost}/system/oauth/token?grant_type=MEMBER`, body.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      }).pipe(
        catchError((error, observable) => this.errorHandler(error, observable)),
        timeout(5000),
        catchError(error => throwError(new Error(`/system/oauth/token?grant_type=MEMBER timeout(5s)`))),
        tap(data => {
          localStorage.setItem('auth_token', data['accessToken']);
          localStorage.setItem('auth_refresh_token', data['refreshToken']);
          localStorage.setItem('auth_way', MEMBER_LOGIN_WAY.CULTURE_CENTER);
          const memberInfo = this.parseToken(data['accessToken']);
          this.memberInfo$.next(memberInfo);
        })
      );
    };
    return obsFunc();
  }

  parseToken(authToken: string) {
    const base64Url = authToken.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    switch (base64.length % 4) {
      case 0: break;
      case 2: base64 += '=='; break;
      case 3: base64 += '='; break;
      default: break;
    }
    return JSON.parse(decodeURIComponent(escape(atob(base64))));
  }

  refreshMemberToken = (refreshToken: any): Observable<any> => {
    return this.http.post<any>(`${this.generatorEnv.apiHost}/system/oauth/token/refresh`, JSON.stringify({}), {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + localStorage.getItem('auth_refresh_token')
      }
    });
  }

  errorHandler = (error: HttpErrorResponse, observable) => {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        switch (error.error.subStatus) {
          case '612':
            // 登入超時
            if (!this._isRefreshing.getValue()) {
              this._isRefreshing.next(true);

              this.refreshMemberToken(localStorage.getItem('auth_refresh_token')).subscribe(data => {
                localStorage.setItem('auth_token', data['accessToken']);
                localStorage.setItem('auth_refresh_token', data['refreshToken']);
                this._isRefreshing.next(false);
              }, error1 => {
                this.globalComponentService.openSanckBar('會員已被登出，請重新登入', 'X', {duration: 5000, horizontalPosition: 'right'});
                localStorage.removeItem('auth_code');
                localStorage.removeItem('auth_token');
                localStorage.removeItem('auth_refresh_token');
                localStorage.removeItem('auth_way');
                this.logout$.next();
                this.memberInfo$.next(undefined);
              });

              const subj = new Subject<any>();
              this._isRefreshing.pipe(
                filter(r => !r),
              ).subscribe(observable.subscribe(subj));

              return subj;
            }
            break;
          default:
            break;
        }
      }
    } else {
      // 未預期的程式錯誤
      console.error(error);
    }

    return throwError(error);
  }
}
